/**
 * Tipos de dominio del Panel Comerciales (vista del frontend).
 *
 * Las categorías de specialty (flow types) se construyen dinámicamente
 * a partir del catálogo `pipedrive_deal_flow_type` que el backend
 * devuelve en `meta.flowTypes` del snapshot.
 */

export interface FlowTypeDef {
    key: string;
    label: string;
}

export type CommercialStatus = 'Activo' | 'Inactivo' | 'Bloqueado';
export const COMMERCIAL_STATUS_OPTIONS: CommercialStatus[] = [
    'Activo',
    'Inactivo',
    'Bloqueado',
];

export interface Commercial {
    id: number;
    name: string;
    team: string;
    active: boolean;
    status: CommercialStatus;
    capacity: number;
    calcLeads: number;
    /**
     * Capacidad # Deals: max deals open en paralelo asignados por el cronjob
     * de scoring. Antes UI "leads efectivos"; el campo conserva el nombre por
     * compatibilidad con el contrato backend.
     */
    leadsEfectivos: number;
    /** Total de deals del seller en `pipedrive_deals` (informativo, no editable). */
    totalDeals?: number;
    /** Deals en status OPEN (informativo, no editable). */
    openDeals?: number;
    /** Deals en status WON/LOST/CLOSED (informativo, no editable). */
    closedDeals?: number;
    specialty: Record<string, number>;
    goal: string;
    absence: string;
    tempBlockUntil?: string;
    notes?: string;
}

export interface Team {
    name: string;
    leader: string;
    members: number;
    goal: string;
    active: boolean;
}

export type VipStatus = CommercialStatus;

export interface VipAccount {
    id: string;
    client: string;
    email: string;
    owner: string;
    team: string;
    reason: string;
    validity: string;
    status: VipStatus;
}

export type NotifType = 'critical' | 'urgent' | 'info' | 'informative';

export const NOTIF_TYPE_LABEL: Record<NotifType, string> = {
    critical: 'Alerta crítica',
    urgent: 'Alerta urgente',
    informative: 'Alerta informativa',
    info: 'Info',
};

export interface Notification {
    id: number;
    date: string;
    type: NotifType;
    message: string;
    team: string;
}

export interface AuditEntry {
    date: string;
    user: string;
    action: string;
    detail: string;
}

export const buildEmptySpec = (flowTypes: FlowTypeDef[]): Record<string, number> =>
    Object.fromEntries(flowTypes.map((ft) => [ft.key, 0]));

export const buildDefaultSpec = (flowTypes: FlowTypeDef[]): Record<string, number> => {
    const spec = buildEmptySpec(flowTypes);
    if (flowTypes.length > 0) spec[flowTypes[0].key] = 100;
    return spec;
};

export const sumSpec = (s: Record<string, number>): number =>
    Object.values(s).reduce((acc, v) => acc + (v || 0), 0);

export const sameSpec = (a: Record<string, number>, b: Record<string, number>): boolean => {
    const allKeys = Object.keys(a).concat(Object.keys(b).filter((k) => !(k in a)));
    return allKeys.every((k) => (a[k] || 0) === (b[k] || 0));
};

export const effectiveLeads = (c: Pick<Commercial, 'calcLeads' | 'capacity'>): number =>
    Math.round((c.calcLeads * c.capacity) / 100);

const PALETTE = [
    '#1E7BFF', '#7C3AED', '#10B981', '#F59E0B',
    '#EC4899', '#F97316', '#06B6D4', '#8B5CF6',
    '#EF4444', '#14B8A6', '#6366F1', '#84CC16',
];

export const flowTypeColor = (index: number): string =>
    PALETTE[index % PALETTE.length];
